import React from 'react';
import { ContactBookResult } from '@/services/api/contacts';
import { User, Building2, Phone, Mail } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ContactBookItemProps {
  contact: ContactBookResult;
  onClick: (contact: ContactBookResult, phone?: string, email?: string) => void;
  mode?: 'dialer' | 'email';
  className?: string;
}

export const ContactBookItem = ({ 
  contact, 
  onClick, 
  mode = 'dialer',
  className 
}: ContactBookItemProps) => {
  const primaryPhone = contact.phones?.find(p => p.isPrimary)?.number || contact.phones?.[0]?.number;
  const primaryEmail = contact.emails?.find(e => e.isPrimary)?.address || contact.emails?.[0]?.address;

  const handleClick = () => {
    onClick(contact, primaryPhone, primaryEmail);
  };

  return (
    <div 
      className={cn(
        "flex items-start gap-3 p-3 hover:bg-accent/50 cursor-pointer border-b border-border/50 last:border-b-0",
        className
      )}
      onClick={handleClick}
    >
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
        <User className="h-4 w-4 text-primary" />
      </div>

      {/* Contact Info */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Name */}
        <div className="font-medium text-sm truncate">
          {contact.full_name}
        </div>

        {/* Lead/Company */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Building2 className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{contact.lead_name}</span>
        </div>

        {/* Designation & Department */}
        {(contact.designation || contact.department) && (
          <div className="text-xs text-muted-foreground truncate">
            {contact.designation}
            {contact.designation && contact.department && ', '}
            {contact.department}
          </div>
        )}

        {/* Primary Contact Method */}
        <div className="flex items-center gap-2 text-xs">
          {mode === 'dialer' && primaryPhone && (
            <div className="flex items-center gap-1 text-primary">
              <Phone className="h-3 w-3" />
              <span className="font-mono">{primaryPhone}</span>
            </div>
          )}
          {mode === 'email' && primaryEmail && (
            <div className="flex items-center gap-1 text-primary">
              <Mail className="h-3 w-3" />
              <span>{primaryEmail}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};